import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMissingMedia } from '../../utils/missingMedia';
import { Download, Image as ImageIcon, RefreshCw, Trash2 } from 'lucide-react';
import { mediaService } from '../../services/mediaService';
import { AuthImage } from './AuthImage';
import { ConfirmModal } from '../common/ConfirmModal';
import { MediaAsset, MediaType, MediaGalleryFilter } from '../../types/media';
import { Button, Card, EmptyState, IconButton, SearchInput, SegmentedControl, Skeleton } from '../ui';
import { MediaTypeTile } from './MediaTypeIcon';

interface MediaGalleryProps {
  stakeholderId?: string;
  organizationId?: string;
  onMediaSelect?: (media: MediaAsset) => void;
  onMediaDelete?: (media: MediaAsset) => void;
  allowDelete?: boolean;
  viewMode?: 'grid' | 'list';
  className?: string;
}

type TypeValue = '' | MediaType;

const TYPE_ITEMS: Array<{ value: TypeValue; label: string }> = [
  { value: '', label: 'All' },
  { value: MediaType.IMAGE, label: 'Images' },
  { value: MediaType.VIDEO, label: 'Videos' },
  { value: MediaType.AUDIO, label: 'Audio' },
  { value: MediaType.DOCUMENT, label: 'Docs' },
];

export const MediaGallery: React.FC<MediaGalleryProps> = ({
  stakeholderId,
  organizationId,
  onMediaSelect,
  onMediaDelete,
  allowDelete = false,
  viewMode = 'grid',
  className = '',
}) => {
  const [allAssets, setMediaAssets] = useState<MediaAsset[]>([]);
  const missingMedia = useMissingMedia();
  const mediaAssets = useMemo(() => allAssets.filter((a) => !missingMedia(a.id)), [allAssets, missingMedia]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<MediaGalleryFilter>({
    stakeholderId,
    organizationId,
  });
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset | null>(null);
  const [confirmMedia, setConfirmMedia] = useState<MediaAsset | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadMediaAssets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const assets = await mediaService.getMediaAssets(filter);
      setMediaAssets(assets);
    } catch (err: any) {
      setError(err.message || 'Failed to load media assets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadMediaAssets();
  }, [loadMediaAssets]);

  const handleFilterChange = (newFilter: Partial<MediaGalleryFilter>) => {
    setFilter(prev => ({ ...prev, ...newFilter }));
  };

  const handleMediaClick = (media: MediaAsset) => {
    setSelectedMedia(media);
    onMediaSelect?.(media);
  };

  const requestDelete = (event: React.MouseEvent, media: MediaAsset) => {
    event.stopPropagation();
    setConfirmMedia(media);
  };

  const renderThumb = (media: MediaAsset, compact = false) =>
    mediaService.isImageFile(media.mimeType) ? (
      <AuthImage mediaId={media.id} alt={media.originalName} className="h-full w-full object-cover" />
    ) : (
      <MediaTypeTile mimeType={media.mimeType} name={media.originalName} compact={compact} />
    );

  const renderGridView = () => (
    <ul className="grid grid-cols-3 gap-0.5 sm:grid-cols-4 sm:gap-2">
      {mediaAssets.map((media) => {
        const selected = selectedMedia?.id === media.id;
        return (
          <li key={media.id} className="group relative">
            <button
              type="button"
              onClick={() => handleMediaClick(media)}
              title={media.originalName}
              aria-label={media.originalName}
              aria-pressed={selected}
              className={`relative block aspect-square w-full overflow-hidden rounded-xl bg-neutral-100 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
                selected ? 'ring-2 ring-primary-600 ring-offset-2' : ''
              }`}
            >
              {renderThumb(media)}
              <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-6 opacity-0 transition-opacity group-hover:opacity-100">
                <span className="block truncate text-[12px] text-white">{media.originalName}</span>
                <span className="block text-[11px] text-white/70">{mediaService.formatFileSize(media.fileSize)}</span>
              </span>
            </button>
            {allowDelete && (
              <button
                type="button"
                onClick={(e) => requestDelete(e, media)}
                aria-label={`Delete ${media.originalName}`}
                title="Delete media"
                className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-md transition-opacity hover:bg-primary-600 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );

  const renderListView = () => (
    <Card divided clip>
      {mediaAssets.map((media) => {
        const selected = selectedMedia?.id === media.id;
        return (
          <div
            key={media.id}
            role="button"
            tabIndex={0}
            onClick={() => handleMediaClick(media)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleMediaClick(media);
            }}
            className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none ${
              selected ? 'bg-primary-50/50' : ''
            }`}
          >
            <span className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-lg bg-neutral-100">{renderThumb(media, true)}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] text-ink" title={media.originalName}>
                {media.originalName}
              </p>
              <p className="truncate text-[12px] text-neutral-400">
                {mediaService.formatFileSize(media.fileSize)} · {new Date(media.createdAt).toLocaleDateString()}
                {media.uploader && <span className="hidden sm:inline"> · Uploaded by {media.uploader.email}</span>}
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center">
              <a
                href={mediaService.getDownloadUrl(media.id)}
                download={media.originalName}
                onClick={(e) => e.stopPropagation()}
                aria-label="Download"
                title="Download"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
              >
                <Download className="h-4 w-4" strokeWidth={1.75} />
              </a>
              {allowDelete && (
                <IconButton
                  label="Delete"
                  size="sm"
                  onClick={(e) => requestDelete(e, media)}
                  className="text-neutral-400 hover:bg-primary-50 hover:text-primary-700"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                </IconButton>
              )}
            </div>
          </div>
        );
      })}
    </Card>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-3 gap-0.5 sm:grid-cols-4 sm:gap-2" aria-busy="true" aria-label="Loading media assets...">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-xl" />
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          <span>Error loading media assets: {error}</span>
          <Button size="xs" variant="secondary" onClick={loadMediaAssets}>
            Retry
          </Button>
        </div>
      );
    }

    if (mediaAssets.length === 0) {
      return <EmptyState icon={ImageIcon} title="No media files found" />;
    }

    return viewMode === 'grid' ? renderGridView() : renderListView();
  };

  return (
    <div className={`media-gallery font-ui ${className}`}>
      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchInput
          className="w-full sm:w-56"
          placeholder="Search files..."
          aria-label="Search files"
          value={filter.search || ''}
          onChange={(e) => handleFilterChange({ search: e.target.value })}
        />
        <SegmentedControl
          label="Type"
          items={TYPE_ITEMS}
          value={(filter.type || '') as TypeValue}
          onChange={(value) => handleFilterChange({ type: value || undefined })}
        />
        <div className="ml-auto flex items-center gap-1">
          {!loading && !error && (
            <span className="text-[12px] tabular-nums text-neutral-400">
              {mediaAssets.length} {mediaAssets.length === 1 ? 'file' : 'files'}
            </span>
          )}
          <IconButton label="Refresh" size="sm" onClick={() => setFilter(prev => ({ ...prev }))}>
            <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        </div>
      </div>

      {renderBody()}

      <ConfirmModal
        open={!!confirmMedia}
        title="Delete file"
        message={<span>Delete “{confirmMedia?.originalName}”? This can’t be undone.</span>}
        confirmText="Delete"
        loading={!!deletingId}
        onCancel={() => setConfirmMedia(null)}
        onConfirm={async () => {
          if (!confirmMedia) return;
          try {
            setDeletingId(confirmMedia.id);
            await mediaService.deleteMediaAsset(confirmMedia.id);
            setMediaAssets(prev => prev.filter(asset => asset.id !== confirmMedia.id));
            onMediaDelete?.(confirmMedia);
            if (selectedMedia?.id === confirmMedia.id) setSelectedMedia(null);
          } catch (err: any) {
            // Optionally display a message modal if used within a page context
          } finally {
            setDeletingId(null);
            setConfirmMedia(null);
          }
        }}
      />
    </div>
  );
};
